import { Box, useControllableState } from "@chakra-ui/react"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef } from "react"
import CreateOrSelectResource from "./CreateOrSelectResource"
import PathCreatorForm, { OnComplete } from "./PathCreatorForm"
import { PathCreatorRawValues } from "./state"

type PathCreatorFlowProps = {
  targetUserId: number
  onComplete?: OnComplete
  initialValue?: PathCreatorRawValues
  setInitialValue?: (value?: PathCreatorRawValues) => void
  // Whether the PathCreatorForm inputs should auto show their related tooltip when
  // user focuses the input.
  autoShowTips?: boolean
}

function PathCreatorFlow({
  targetUserId,
  onComplete,
  initialValue,
  setInitialValue,
  autoShowTips
}: PathCreatorFlowProps) {
  // Handle both the initialValue prop and state, which could be updated by
  // the CreateOrSelectResource component.
  // Using null value for resource/cohort to indicate user is explicitly choosing to select existing resource,
  // rather than create a new one.
  // Using null value for initialValue to allow user to create or select a resource/cohort.
  const [initialFormValue, setInitialFormValue] = useControllableState<
    PathCreatorRawValues | undefined
  >({
    value: initialValue,
    onChange: setInitialValue
  })

  const containerRef = useRef<HTMLDivElement>(null)

  // When transitioning from gate to form, scroll the form into view
  // since the form is taller and may extend below the viewport.
  useEffect(() => {
    if (initialFormValue && containerRef.current) {
      requestAnimationFrame(() => {
        const container = containerRef.current
        if (!container) return
        const scrollParent = container.closest(
          ".ScrollView"
        ) as HTMLElement | null
        if (!scrollParent) return
        const containerRect = container.getBoundingClientRect()
        const parentRect = scrollParent.getBoundingClientRect()
        // Account for any fixed toolbar at the bottom of the screen
        // (e.g. mobile augmentation toolbar) by checking for it and
        // subtracting its height from the available space.
        const toolbar = document.querySelector(
          ".ToolbarBottom"
        ) as HTMLElement | null
        const toolbarHeight = toolbar?.offsetHeight ?? 0
        const availableBottom = parentRect.bottom - toolbarHeight
        const overflowBottom = containerRect.bottom - availableBottom
        if (overflowBottom > 0) {
          scrollParent.scrollBy({
            top: overflowBottom + 40,
            behavior: "smooth"
          })
        }
      })
    }
  }, [initialFormValue])

  return (
    <Box ref={containerRef} className="PathCreatorFlow" pos="relative" w="100%">
      <AnimatePresence mode="popLayout" initial={false}>
        {initialFormValue ? (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <PathCreatorForm
              targetUserId={targetUserId}
              onComplete={onComplete}
              initialValue={initialFormValue}
              autoShowTips={autoShowTips}
            />
          </motion.div>
        ) : (
          <motion.div
            key="gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CreateOrSelectResource onSelect={setInitialFormValue} />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default PathCreatorFlow
